import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { createPost, deleteApprovedPost, listPublishedPosts } from "./blog/blogFirebase";
import { useBlogTask } from "./blog/useBlogTask";
import { usePendingPosts } from "./blog/usePendingPosts";
import ConfirmModal from "./blog/ConfirmModal";
import BlogPostRow from "./blog/BlogPostRow";
import PendingPostsSection from "./blog/PendingPostsSection";
import RefreshPostsSection from "./blog/RefreshPostsSection";
import Config from "../../utils/Config";

const AdminBlogReview = () => {
  const { newPosts, refreshPosts, loading } = usePendingPosts();
  const [publishedPosts, setPublishedPosts] = useState([]);
  const [creating, setCreating]             = useState(false);
  const [confirmModal, setConfirmModal]     = useState(null);
  const [deletingId, setDeletingId]         = useState(null);
  const navigate = useNavigate();
  const { taskMsg, startTask, stopTask }    = useBlogTask();

  const handleCreate = async () => {
    setCreating(true);
    const taskId = startTask();
    try {
      const result = await createPost({ taskId });
      navigate(`/blog/${result.data.newDocId}`);
    } catch (e) {
      console.error("createBlogPost error:", e);
      setCreating(false);
    } finally {
      stopTask();
    }
  };

  // Published posts come from Sanity (source of truth) via a callable.
  // No real-time subscription: the live blog only changes through admin
  // actions here, so we refetch after a successful delete instead.
  const loadPublishedPosts = useCallback(async () => {
    try {
      const result = await listPublishedPosts();
      setPublishedPosts(result.data?.posts || []);
    } catch (e) {
      console.error("listPublishedPosts error:", e);
    }
  }, []);

  useEffect(() => {
    loadPublishedPosts();
  }, [loadPublishedPosts]);

  const openDeleteConfirm = (post) => {
    setConfirmModal({
      title: "Delete Published Post",
      message: `"${post.title}" will be permanently deleted from the live blog. This cannot be undone.`,
      confirmLabel: "🗑 Delete",
      confirmClass: "bg-red-600 hover:bg-red-700",
      onConfirm: () => doDeletePublished(post),
    });
  };

  const doDeletePublished = async (post) => {
    setConfirmModal(null);
    setDeletingId(post.id);
    try {
      await deleteApprovedPost({ docId: post.id, wpPostId: post.wpPostId, slug: post.slug });
      await loadPublishedPosts();
    } catch (e) {
      console.error("deleteBlogPost error:", e);
    } finally {
      setDeletingId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-16">
        <div className="text-xl text-gray-500">Loading posts…</div>
      </div>
    );
  }

  return (
    <div className="relative">
      {creating && (
        <div className="absolute inset-0 z-50 flex flex-col items-center justify-start pt-6 bg-white/60 backdrop-blur-sm rounded-lg">
          <div className="text-center px-6">
            <div className="w-14 h-14 border-4 border-purple-200 border-t-purple-600 rounded-full animate-spin mx-auto mb-5" />
            <p className="text-gray-700 text-base font-semibold mb-2">✍️ Generating new blog post…</p>
            {taskMsg && (
              <p className="text-gray-600 text-sm mb-1">{taskMsg}</p>
            )}
            <p className="text-gray-500 text-sm">This may take a minute — please don't close this page.</p>
          </div>
        </div>
      )}
      {confirmModal && (
        <ConfirmModal
          title={confirmModal.title}
          message={confirmModal.message}
          confirmLabel={confirmModal.confirmLabel}
          confirmClass={confirmModal.confirmClass}
          onConfirm={confirmModal.onConfirm}
          onCancel={() => setConfirmModal(null)}
        />
      )}

      {/* ── New posts ───────────────────────────────────────────────────── */}
      <PendingPostsSection
        posts={newPosts}
        onCreate={handleCreate}
        onWrite={() => window.open("https://toon-kids.sanity.studio", "_blank", "noopener,noreferrer")}
        onOpen={(id) => navigate(`/blog/${id}`)}
      />

      {/* ── Content refreshes ───────────────────────────────────────────── */}
      <RefreshPostsSection
        posts={refreshPosts}
        onOpen={(id) => navigate(`/blog/${id}`)}
      />

      {/* ── Published section ────────────────────────────────────────────── */}
      <div className="mb-5">
        <h2 className="text-xl font-bold text-gray-800">Published Posts</h2>
        <p className="text-sm text-gray-500 mt-0.5">
          Live on the blog. Delete permanently removes the post and its image.
        </p>
      </div>

      {publishedPosts.length === 0 && (
        <div className="text-center py-12 bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="text-5xl mb-4">📄</div>
          <p className="text-gray-500 text-lg">No published posts yet.</p>
        </div>
      )}

      {publishedPosts.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 divide-y divide-gray-100">
          {publishedPosts.map(post => (
            <BlogPostRow
              key={post.id}
              post={post}
              onClick={() => window.open(`${Config.domain}/blog/${post.slug || post.id}`, "_blank", "noopener,noreferrer")}
              meta={<p className="text-xs text-gray-400 mt-0.5">{post.date} · {post.readTime}</p>}
              actions={
                <button
                  onClick={(e) => { e.stopPropagation(); openDeleteConfirm(post); }}
                  disabled={deletingId === post.id}
                  className="shrink-0 px-3 py-1.5 text-sm font-medium text-red-600 border border-red-200 rounded-lg hover:bg-red-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  title="Delete this post"
                >
                  {deletingId === post.id ? "Deleting…" : "🗑 Delete"}
                </button>
              }
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminBlogReview;
